import { type AxiosRequestConfig, type AxiosResponse, isAxiosError } from 'axios'

import apiBase from './base'

import type {
  RequestFor,
  ResponseFor,
  PathsForMethod,
} from './types/api'
import type { RootState } from '~/app/store'

export function apiGet<P extends PathsForMethod<'get'>>(
  url: P,
  config?: AxiosRequestConfig<RequestFor<P, 'get'>>,
) {
  return apiBase.get<ResponseFor<P, 'get'>, AxiosResponse<ResponseFor<P, 'get'>>>(url, config)
}

export function apiPost<P extends PathsForMethod<'post'>>(
  url: P,
  data?: RequestFor<P, 'post'>,
  config?: AxiosRequestConfig<RequestFor<P, 'post'>>,
) {
  return apiBase.post<ResponseFor<P, 'post'>, AxiosResponse<ResponseFor<P, 'post'>>, RequestFor<P, 'post'>>(
    url,
    data,
    config,
  )
}

export function apiPut<P extends PathsForMethod<'put'>>(
  url: P,
  data?: RequestFor<P, 'put'>,
  config?: AxiosRequestConfig<RequestFor<P, 'put'>>,
) {
  return apiBase.put<ResponseFor<P, 'put'>, AxiosResponse<ResponseFor<P, 'put'>>, RequestFor<P, 'put'>>(
    url,
    data,
    config,
  )
}

export function apiPatch<P extends PathsForMethod<'patch'>>(
  url: P,
  data?: RequestFor<P, 'patch'>,
  config?: AxiosRequestConfig<RequestFor<P, 'patch'>>,
) {
  return apiBase.patch<ResponseFor<P, 'patch'>, AxiosResponse<ResponseFor<P, 'patch'>>, RequestFor<P, 'patch'>>(
    url,
    data,
    config,
  )
}

export function apiDelete<P extends PathsForMethod<'delete'>>(
  url: P,
  config?: AxiosRequestConfig<RequestFor<P, 'delete'>>,
) {
  return apiBase.delete<ResponseFor<P, 'delete'>, AxiosResponse<ResponseFor<P, 'delete'>>, RequestFor<P, 'delete'>>(
    url,
    config,
  )
}

/** Axios response helpers */

export const requireResponseData = <T>(
  data: T | '' | null | undefined,
  requestDescription: string,
): T => {
  if (data === '' || data === null || data === undefined)
    throw new Error(`Empty response while ${requestDescription}`)

  return data
}

export const isResponseMissingAuthorization = (response: AxiosResponse<unknown>): boolean => {
  // NOTE: since 401 is used by axios to trigger token refresh, 401 responses
  // will be captured and handled in the middleware so the backend should not
  // return 401 for other reasons
  return response.status === 401
}

export const isResponseValidationError = (response: AxiosResponse<unknown>): boolean => {
  return response.status === 422
}

export const isResponseUnexpected = (response: AxiosResponse<unknown>): boolean => {
  // Could be clever here and base on OpenAPI spec but for now just check for
  // status codes (assuming 2xx and 3xx are expected)
  return (
    response.status >= 400
    && !isResponseMissingAuthorization(response)
    && !isResponseValidationError(response)
  )
}

export { isAxiosError }

export const isAxios404Error = (error: unknown): boolean => {
  if (isAxiosError(error))
    return error.response?.status === 404
  return false
}

/** Redux helpers */

export interface ConditionOptions {
  getState: () => RootState
  extra: unknown
}

interface ReduxError {
  code: string
  message: string
  name: string
  stack: string
}

interface ReduxRequestError extends ReduxError {
  code: 'ERR_BAD_REQUEST'
  name: 'AxiosError'
}

interface ReduxResponseError extends ReduxError {
  code: 'ERR_BAD_RESPONSE'
  name: 'AxiosError'
}

export const isReduxError = (error: unknown): error is ReduxError => {
  return (
    // is an object
    typeof error === 'object' && !Array.isArray(error) && error !== null
    // contains all of the right fields
    && 'code' in error && typeof error.code === 'string'
    && 'name' in error && typeof error.name === 'string'
    && 'message' in error && typeof error.message === 'string'
    && 'stack' in error && typeof error.stack === 'string'
  )
}

// 4xx
export const isReduxRequestError = (error: unknown): error is ReduxRequestError => {
  return isReduxError(error) && error.code === 'ERR_BAD_REQUEST'
}

export const isReduxRequestHttpError = (error: unknown, httpCode = 404): error is ReduxRequestError => {
  if (typeof error === 'string')
    return error.includes(httpCode.toString())
  return isReduxRequestError(error) && error.message.includes(httpCode.toString())
}

// 5xx
export const isReduxResponseError = (error: unknown): error is ReduxResponseError => {
  return isReduxError(error) && error.code === 'ERR_BAD_RESPONSE'
}

import { isValidationIssue, getFieldErrors, handleFieldErrors } from './schema'
export { isValidationIssue, getFieldErrors, handleFieldErrors }

export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
}

export default api
